import { useState } from 'react';
import { Input } from '@/components/ui/Input';

export const MESSAGE_TEMPLATE_VARIABLES = [
  { code: '{{imie}}', description: 'Imię klienta' },
  { code: '{{nazwisko}}', description: 'Nazwisko klienta' },
  { code: '{{imie_nazwisko}}', description: 'Imię i nazwisko' },
  { code: '{{email}}', description: 'E-mail klienta' },
  { code: '{{telefon}}', description: 'Telefon klienta' },
  { code: '{{numer_zamowienia}}', description: 'Numer zamówienia' },
  { code: '{{data_zamowienia}}', description: 'Data zamówienia' },
  { code: '{{produkty}}', description: 'Lista produktów w HTML' },
  { code: '{{produkty_lista}}', description: 'Lista produktów jako tekst' },
  { code: '{{wartosc}}', description: 'Wartość zamówienia' },
  { code: '{{metoda_platnosci}}', description: 'Metoda płatności' },
  { code: '{{metoda_wysylki}}', description: 'Metoda wysyłki' },
  { code: '{{tracking}}', description: 'Numer śledzenia' },
  { code: '{{link_sledzenia}}', description: 'Klikalny link śledzenia' },
  { code: '{{adres}}', description: 'Pełny adres dostawy' },
  { code: '{{ulica}}', description: 'Ulica' },
  { code: '{{miasto}}', description: 'Miasto' },
  { code: '{{kod_pocztowy}}', description: 'Kod pocztowy' },
  { code: '{{kraj}}', description: 'Kraj' },
  { code: '{{sklep}}', description: 'Nazwa sklepu' },
  { code: '{{sklep_email}}', description: 'E-mail sklepu' },
  { code: '{{sklep_telefon}}', description: 'Telefon sklepu' },
  { code: '{{konto_bankowe}}', description: 'Numer konta bankowego sklepu' },
  { code: '{{status}}', description: 'Status zamówienia' },
  { code: '{{notatka}}', description: 'Notatki zamówienia' },
  { code: '{{link_panelu_klienta}}', description: 'Pełny panel klienta: zamówienia, zwroty, reklamacje i dostępne opcje' },
  { code: '{{link_ponownej_wysylki}}', description: 'Klikalny link ponownej wysyłki' },
  { code: '{{koszt_ponownej_wysylki}}', description: 'Koszt ponownej wysyłki po nieodebraniu' },
  { code: '{{potracenie_za_nieodebrana_przesylke}}', description: 'Potrącenie po rezygnacji z ponownej wysyłki' },
] as const;

const VARIABLE_GROUPS = [
  { title: 'Klient', keys: ['imie', 'nazwisko', 'imie_nazwisko', 'email', 'telefon'] },
  { title: 'Zamówienie i płatność', keys: ['numer_zamowienia', 'data_zamowienia', 'produkty', 'produkty_lista', 'wartosc', 'metoda_platnosci', 'status', 'notatka'] },
  { title: 'Dostawa i adres', keys: ['metoda_wysylki', 'tracking', 'link_sledzenia', 'adres', 'ulica', 'miasto', 'kod_pocztowy', 'kraj'] },
  { title: 'Panel klienta i ponowna wysyłka', keys: ['link_panelu_klienta', 'link_ponownej_wysylki', 'koszt_ponownej_wysylki', 'potracenie_za_nieodebrana_przesylke'] },
  { title: 'Sklep', keys: ['sklep', 'sklep_email', 'sklep_telefon', 'konto_bankowe'] },
];

interface MessageTemplateVariablePickerProps {
  onInsert: (code: string) => void;
}

export function MessageTemplateVariablePicker({ onInsert }: MessageTemplateVariablePickerProps) {
  const [search, setSearch] = useState('');
  const query = search.trim().toLocaleLowerCase('pl');
  const groups = VARIABLE_GROUPS.map(group => ({
    ...group,
    variables: MESSAGE_TEMPLATE_VARIABLES.filter(variable =>
      group.keys.some(key => variable.code === `{{${key}}}`)
      && `${group.title} ${variable.code} ${variable.description}`.toLocaleLowerCase('pl').includes(query)),
  })).filter(group => group.variables.length > 0);

  return (
    <div className="mt-2 rounded-lg border border-border-subtle bg-surface-raised/30 p-3">
      <p className="mb-2 text-xs font-medium text-text-secondary">Dostępne znaczniki. Kliknij, aby wstawić do treści:</p>
      <Input className="mb-3 w-full" type="search" aria-label="Szukaj znacznika" placeholder="Szukaj znacznika lub opisu..." value={search} onChange={event => setSearch(event.target.value)} />
      <div className="max-h-80 space-y-3 overflow-y-auto">
        {groups.map(group => (
          <section key={group.title} aria-label={group.title}>
            <h4 className="mb-1.5 text-xs font-semibold text-text-secondary">{group.title}</h4>
            <div className="grid grid-cols-1 gap-1.5 sm:grid-cols-2">
              {group.variables.map(variable => (
                <button key={variable.code} type="button" onClick={() => onInsert(variable.code)} title={variable.description}
                  className="rounded border border-border-subtle bg-surface-hover/60 px-2 py-1.5 text-left transition-colors hover:border-accent-primary/40 hover:bg-accent-primary/10 focus-visible:ring-2 focus-visible:ring-accent-primary/40">
                  <code className="block break-all text-xxs text-accent-primary">{variable.code}</code>
                  <span className="text-xxs text-text-muted">{variable.description}</span>
                </button>
              ))}
            </div>
          </section>
        ))}
        {groups.length === 0 && <p className="text-xs text-text-muted">Brak pasujących znaczników.</p>}
      </div>
    </div>
  );
}
